import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatSgd, apiErr } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { HazardsPanel, formatBandColor, bandBorder } from "../components/climate/HazardsPanel";
import { OcrRegister } from "../components/climate/OcrRegister";
import { ResilienceAgent } from "../components/climate/ResilienceAgent";
import { Facility360 } from "../components/Facility360";
import {
  Building2, CloudRain, ShieldAlert, Banknote, Sprout, FolderKanban, RefreshCw, Globe2, Factory,
} from "lucide-react";


function Tile({ label, value, icon: Icon, tone, testid, pkey }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow"><Icon className="h-3.5 w-3.5" />{label}{pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold mt-1.5 ${tone || ""}`}>{value}</div>
    </div>
  );
}

function ScoreBar({ score }) {
  const w = Math.min(100, score);
  const color = score >= 80 ? "bg-red-500" : score >= 60 ? "bg-orange-400" : score >= 40 ? "bg-amber-400" : "bg-accent";
  return (
    <div className="h-1.5 w-full bg-border rounded-full overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${w}%` }} />
    </div>
  );
}

function FacilityDrilldown({ facility, onClose }) {
  const f = facility;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="facility-drilldown">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <Factory className="h-4 w-4 text-accent" />{f.name}
            <span className={`font-mono text-lg ${formatBandColor(f.band)}`}>{f.score}</span>
            <Badge variant="outline" className={`text-[9px] uppercase ${formatBandColor(f.band)}`}>{f.band}</Badge>
          </SheetTitle>
        </SheetHeader>
        <div className="text-xs text-muted-foreground mt-1">{f.location} · {f.country}</div>

        <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
          {[["Asset value", formatSgd(f.asset_value_sgd)], ["Plant inventory", formatSgd(f.plant_inventory_value_sgd)],
            ["Revenue exposure", formatSgd(f.revenue_exposure_sgd)], ["Capacity share", `${f.capacity_share_pct}%`],
            ["Resilience", `${f.resilience_rating}/5`], ["Adaptive capacity", `${f.adaptive_capacity}/5`],
            ["Criticality", `${f.criticality}/5`], ["Workforce", f.workforce_count]].map(([k, v]) => (
            <div key={k} className="card-flat p-2.5"><div className="label-eyebrow">{k}</div><div className="font-mono font-semibold mt-0.5">{v}</div></div>
          ))}
        </div>

        <div className="label-eyebrow mt-5 mb-2">Hazard exposure & score explanation</div>
        <div className="space-y-2">
          {f.hazards.map((m) => (
            <div key={m.hazard_id} className={`card-flat p-3 border-l-2 ${bandBorder(m.band)}`} data-testid={`drill-hazard-${m.hazard_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <CloudRain className="h-3.5 w-3.5 text-muted-foreground" />{m.hazard_name}
                </div>
                <span className={`font-mono font-bold ${formatBandColor(m.band)}`}>{m.score} · {m.band}</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5 mt-2 text-center">
                {[["H", m.breakdown.hazard_severity], ["E", m.breakdown.exposure], ["V", m.breakdown.vulnerability],
                  ["C", m.breakdown.criticality], ["A", m.breakdown.adaptive_capacity]].map(([k, v]) => (
                  <div key={k} className="bg-surface border border-border rounded-sm py-1.5">
                    <div className="text-[9px] text-muted-foreground">{k}</div>
                    <div className="font-mono text-xs font-bold">{v}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2 font-mono">
                Asset at risk {formatSgd(m.asset_value_at_risk)} · plant {formatSgd(m.plant_at_risk)} · revenue {formatSgd(m.revenue_exposed)}
              </div>
              {m.projects_affected?.length > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">Projects: {m.projects_affected.join(", ")}</div>
              )}
            </div>
          ))}
          {!f.hazards.length && <div className="text-xs text-muted-foreground">No active hazard covers this location.</div>}
        </div>

        <div className="label-eyebrow mt-5 mb-2">Asset classes</div>
        {[["Buildings", f.buildings], ["Biological assets", f.biological_assets], ["Machinery", f.machinery],
          ["Vehicles", f.vehicles], ["Linked projects", f.linked_projects]].map(([k, arr]) => (
          <div key={k} className="text-xs mb-1.5">
            <span className="text-muted-foreground">{k}:</span>{" "}
            {arr?.length ? arr.join(" · ") : <span className="text-muted-foreground/50">none</span>}
          </div>
        ))}
      </SheetContent>
    </Sheet>
  );
}

export default function ClimateOperations() {
  const { user } = useAuth();
  const canEdit = ["tenant_admin", "esg_manager"].includes(user?.role);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [master, setMaster] = useState({});
  const [f360, setF360] = useState(null);
  const load = useCallback(() => {
    api.get("/climate-risk/overview")
      .then((r) => setD(r.data))
      .catch((e) => { if (e?.response?.status !== 402) toast.error(apiErr(e)); setD({ enabled: false, gated: e?.response?.status === 402 }); })
      .finally(() => setLoading(false));
    api.get("/facilities").then((r) => {
      setMaster(Object.fromEntries(r.data.facilities.map((f) => [f.name.trim().toLowerCase(), f])));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const masterOf = (name) => master[(name || "").trim().toLowerCase()];
  const FacName = ({ name, testid }) => {
    const m = masterOf(name);
    if (!m) return <span className="font-semibold text-sm truncate">{name}</span>;
    return (
      <span role="link" tabIndex={0} data-testid={testid}
        className="font-semibold text-sm truncate underline decoration-dotted underline-offset-2 hover:text-accent cursor-pointer transition-colors"
        onClick={(e) => { e.stopPropagation(); setF360(m); }}>
        {name}
      </span>
    );
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Scoring your operations…</div>;
  if (!d?.enabled) {
    return (
      <div className="p-8 max-w-lg" data-testid="climate-disabled">
        <div className="label-eyebrow mb-2">Climate Risk · My Operations</div>
        <h1 className="font-display text-2xl font-bold">Own-Company Climate Risk is not enabled</h1>
        <p className="text-sm text-muted-foreground mt-3">
          {d?.gated
            ? "This engine is part of the Climate Scenarios capability, which is not included in your current plan."
            : "The Own-Company Climate Risk engine is switched off for this workspace. An administrator can enable it, after which your facilities are scored against active climate hazards."}
        </p>
      </div>
    );
  }
  const t = d.tiles;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="climate-operations-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow mb-1">Climate Risk · My Operations</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Own-Company Climate Control Tower</h1>
          <p className="text-xs text-muted-foreground mt-1">Digital twin of your facilities scored against active hazards — H/E/V/C/A methodology, fully explainable.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="climate-refresh-btn"><RefreshCw className="h-3.5 w-3.5" /> Re-score</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Facilities monitored" value={t.facilities_monitored} icon={Building2} testid="tile-facilities" pkey="climate.facilities" />
        <Tile label="Active hazards" value={t.active_hazards} icon={CloudRain} tone="text-amber-400" testid="tile-hazards" pkey="climate.hazards" />
        <Tile label="High-risk facilities" value={t.high_risk_facilities} icon={ShieldAlert} tone="text-red-500" testid="tile-high-risk" pkey="climate.high_risk" />
        <Tile label="Facilities exposed" value={t.facilities_exposed} icon={Globe2} testid="tile-exposed" pkey="climate.exposed" />
        <Tile label="Asset value exposed" value={formatSgd(t.asset_value_exposed)} icon={Banknote} tone="text-orange-400" testid="tile-asset-exposed" pkey="climate.asset_exposed" />
        <Tile label="Plant climate-at-risk" value={formatSgd(t.plant_at_risk)} icon={Sprout} tone="text-orange-400" testid="tile-plant-risk" pkey="climate.plant_risk" />
        <Tile label="Projects affected" value={t.projects_affected} icon={FolderKanban} testid="tile-projects" pkey="climate.projects" />
        <Tile label="Revenue exposed" value={formatSgd(t.revenue_exposed)} icon={Banknote} testid="tile-revenue" pkey="climate.revenue" />
      </div>

      <Tabs defaultValue="facilities">
        <TabsList>
          <TabsTrigger value="facilities" data-testid="climate-tab-facilities">Facilities</TabsTrigger>
          <TabsTrigger value="twin" data-testid="climate-tab-twin">Digital Twin</TabsTrigger>
          <TabsTrigger value="hazards" data-testid="climate-tab-hazards">Hazards</TabsTrigger>
          <TabsTrigger value="register" data-testid="climate-tab-register">OCR Register ({d.register_summary.total})</TabsTrigger>
          <TabsTrigger value="agent" data-testid="climate-tab-agent">Resilience Agent</TabsTrigger>
        </TabsList>

        <TabsContent value="facilities" className="mt-4 space-y-2">
          {[...d.facilities].sort((a, b) => b.score - a.score).map((f) => (
            <button key={f.id} onClick={() => setDrill(f)} data-testid={`facility-row-${f.id}`}
              className={`w-full text-left card-flat p-4 border-l-2 ${bandBorder(f.band)} hover:bg-surface-hover transition-colors`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Factory className="h-4 w-4 text-muted-foreground shrink-0" />
                  <FacName name={f.name} testid={`climate-fac-360-${f.id}`} />
                  <Badge variant="outline" className="text-[9px] uppercase">{f.country}</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-muted-foreground font-mono hidden sm:inline">
                    {f.hazards.length} hazard(s) · asset {formatSgd(f.asset_value_sgd)}
                  </span>
                  <span className={`font-mono font-bold ${formatBandColor(f.band)}`}>{f.score}</span>
                  <span className={`text-[10px] uppercase tracking-wide w-16 ${formatBandColor(f.band)}`}>{f.band}</span>
                </div>
              </div>
              <div className="mt-2"><ScoreBar score={f.score} /></div>
            </button>
          ))}
        </TabsContent>

        <TabsContent value="twin" className="mt-4 space-y-4">
          {d.hierarchy.map((c) => (
            <div key={c.country} data-testid={`twin-country-${c.country}`}>
              <div className="label-eyebrow mb-2 flex items-center gap-2"><Globe2 className="h-3.5 w-3.5" />{c.country} · {c.facilities.length} facility(ies)</div>
              <div className="grid md:grid-cols-2 gap-3">
                {c.facilities.map((f) => (
                  <div key={f.id} className={`card-flat p-4 border-l-2 ${bandBorder(f.band)}`}>
                    <div className="flex items-center justify-between">
                      <FacName name={f.name} testid={`climate-twin-360-${f.id}`} />
                      <span className={`font-mono font-bold ${formatBandColor(f.band)}`}>{f.score}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{f.location}</div>
                    <div className="grid grid-cols-3 gap-1.5 mt-3 text-center text-[10px]">
                      {[["Buildings", f.buildings.length], ["Bio assets", f.biological_assets.length],
                        ["Machinery", f.machinery.length], ["Vehicles", f.vehicles.length],
                        ["Workforce", f.workforce_count], ["Projects", f.linked_projects.length]].map(([k, v]) => (
                        <div key={k} className="bg-surface border border-border rounded-sm py-1.5">
                          <div className="text-muted-foreground">{k}</div>
                          <div className="font-mono font-bold text-xs">{v}</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono mt-2">
                      Capacity {f.capacity_share_pct}% · resilience {f.resilience_rating}/5 · criticality {f.criticality}/5
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="hazards" className="mt-4">
          <HazardsPanel hazards={d.hazards} types={d.hazard_type_options || []} onChanged={load} />
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <RegisterTab />
        </TabsContent>

        <TabsContent value="agent" className="mt-4 max-w-3xl">
          <ResilienceAgent />
        </TabsContent>
      </Tabs>

      {drill && <FacilityDrilldown facility={drill} onClose={() => setDrill(null)} />}
      {f360 && <Facility360 facility={f360} canEdit={canEdit} onClose={() => setF360(null)} onSaved={load} />}
    </div>
  );
}

function RegisterTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/climate-risk/register").then((r) => setRows(r.data.register)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return <OcrRegister register={rows} onChanged={load} />;
}
